import { useState, useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import { CATALOG, CATEGORIES } from './connectorCatalog.js'
import BrandLogo from './BrandLogo.jsx'
import styles from './ConnectorCatalogModal.module.css'

export default function ConnectorCatalogModal({ isOpen, onClose, activeCounts = {}, onConnect, enabledOverrides = {} }) {
  const { apiFetch } = useAuth()
  const [search, setSearch]       = useState('')
  const [category, setCategory]   = useState('All')
  const [notified, setNotified]   = useState({})   // { [id]: true }
  const [requested, setRequested] = useState(false)
  const [reqName, setReqName]     = useState('')
  const [reqUseCase, setReqUseCase] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Fix 3: close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const filtered = CATALOG.filter(c => {
    const isEnabled = enabledOverrides[c.id] ?? c.enabled ?? true
    if (!isEnabled) return false
    const matchCat = category === 'All' || c.category === category
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
  })

  const available   = filtered.filter(c => c.status === 'available')
  const comingSoon  = filtered.filter(c => c.status === 'coming_soon')

  const handleNotify = useCallback(async (provider) => {
    setNotified(n => ({ ...n, [provider.id]: true }))
    try {
      await apiFetch('/api/connectors/notify', {
        method: 'POST',
        body: JSON.stringify({ provider_id: provider.id }),
      })
    } catch {
      // best-effort — notification preference already shown in UI
    }
  }, [apiFetch])

  const handleRequest = async (e) => {
    e.preventDefault()
    if (!reqName.trim()) return
    setSubmitting(true)
    try {
      await apiFetch('/api/connectors/requests', {
        method: 'POST',
        body: JSON.stringify({ connector_name: reqName.trim(), use_case: reqUseCase.trim() }),
      })
      setRequested(true)
    } catch {
      setRequested(true) // show success anyway — don't block UX on API errors
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Browse Connectors</div>
            <div className={styles.sub}>{CATALOG.length} integrations available or in progress</div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Search + filter */}
        <div className={styles.controls}>
          <input
            className={styles.search}
            placeholder="Search connectors…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className={styles.pills}>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`${styles.pill} ${category === cat ? styles.pillActive : ''}`}
                onClick={() => setCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className={styles.body}>

          {/* Available */}
          {available.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                <span className={styles.dot} style={{ background: 'var(--color-success)' }} />
                Available Now
              </div>
              <div className={styles.grid}>
                {available.map(item => {
                  const count = activeCounts[item.id] ?? 0
                  return (
                    <div key={item.id} className={`${styles.card} ${count > 0 ? styles.cardConnected : ''}`} title={item.description}>
                      <BrandLogo id={item.id} fallback={item.icon} size={28} className={styles.cardIcon} />
                      <div className={styles.cardName}>{item.name}</div>
                      <div className={styles.cardCat}>{item.category}</div>
                      {count > 0 && (
                        <div className={styles.instanceBadge}>✓ {count} connected</div>
                      )}
                      <button
                        className={count > 0 ? styles.addAnotherBtn : styles.connectBtn}
                        onClick={() => { onClose(); onConnect(item) }}
                      >
                        {count > 0 ? '+ Add another' : 'Connect →'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* Coming soon */}
          {comingSoon.length > 0 && (
            <section className={styles.section}>
              <div className={styles.sectionLabel}>
                <span className={styles.dot} style={{ background: 'var(--color-warn)' }} />
                Coming Soon
              </div>
              <div className={styles.grid}>
                {comingSoon.map(item => (
                  <div key={item.id} className={`${styles.card} ${styles.cardDim}`}>
                    <BrandLogo id={item.id} fallback={item.icon} size={28} className={styles.cardIcon} />
                    <div className={styles.cardName}>{item.name}</div>
                    <div className={styles.cardCat}>{item.category}</div>
                    {notified[item.id] ? (
                      <div className={styles.notifiedMsg}>✓ We&apos;ll notify you</div>
                    ) : (
                      <button
                        className={styles.notifyBtn}
                        onClick={() => handleNotify(item)}
                      >
                        Notify me
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {available.length === 0 && comingSoon.length === 0 && (
            <div className={styles.noResults}>No connectors match &quot;{search}&quot;</div>
          )}

          {/* Request */}
          <section className={styles.requestSection}>
            <div className={styles.requestTitle}>Don&apos;t see what you need?</div>
            <div className={styles.requestSub}>
              Tell us what you&apos;d like to connect and we&apos;ll prioritise it.
            </div>
            {requested ? (
              <div className={styles.requestSuccess}>
                ✓ Request submitted — we&apos;ll be in touch when it&apos;s ready.
              </div>
            ) : (
              <form className={styles.requestForm} onSubmit={handleRequest}>
                <input
                  className={styles.requestInput}
                  placeholder="Name of system (e.g. Epicor, Fishbowl, Vend)"
                  value={reqName}
                  onChange={e => setReqName(e.target.value)}
                  required
                />
                <textarea
                  className={styles.requestTextarea}
                  placeholder="What data do you need from it? (optional)"
                  value={reqUseCase}
                  onChange={e => setReqUseCase(e.target.value)}
                  rows={3}
                />
                <button
                  type="submit"
                  className={styles.requestSubmit}
                  disabled={submitting || !reqName.trim()}
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </form>
            )}
          </section>

        </div>
      </div>
    </div>,
    document.body
  )
}
